import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  useActionData,
  useLoaderData,
} from 'react-router'
import { getCaptchaProtectedApiClient } from '~/api/apiClient.server'
import {
  ClearedErrorData,
  getApiErrorMessage,
  isErrorCodeKnown,
  returnKnownErrorOrThrowErrorResponse,
} from '~/api/apiError'
import {
  authenticateWithTokens,
  requireNotAuthenticated,
} from '~/auth/session.server'
import {
  BackgroundElements,
  PageLayoutWithoutHeader,
} from '~/components/Layout'
import { ChangePasswordEnum } from '~/dashboard/ChangePassword'
import { Footer } from '~/dashboard/Footer'
import { LoginForm, LoginIntentENUM } from '~/dashboard/LoginForm'

type LoaderReturnType = {
  connectionError: ClearedErrorData | undefined
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const email = formData.get('email') as string
  const password = formData.get('password') as string
  const token = (formData.get('_captcha') as string) || ''
  const intent = formData.get('intent') as LoginIntentENUM | ChangePasswordEnum

  if (intent === ChangePasswordEnum.REQUEST_RESET_PASSWORD) {
    try {
      await getCaptchaProtectedApiClient(token).default.requestPasswordReset({
        requestBody: { email },
      })

      return { success: true }
    } catch (error) {
      return returnKnownErrorOrThrowErrorResponse(error)
    }
  }
  if (intent === LoginIntentENUM.LOGIN) {
    if (!email || !password) {
      return { message: 'E-Mail und Passwort sind erforderlich.' }
    }

    try {
      const { accessToken, refreshToken } = await getCaptchaProtectedApiClient(
        token,
      ).default.create({
        requestBody: { email, password },
      })

      if (!accessToken || !refreshToken) {
        return { message: 'Ungültige Antwort vom Server.' }
      }

      await authenticateWithTokens(request, accessToken, refreshToken)
    } catch (error) {
      // authenticateWithTokens throws a redirect - don't catch it
      if (error instanceof Response) {
        throw error
      }
      return returnKnownErrorOrThrowErrorResponse(error)
    }
  }
}

export async function loader({ request }: LoaderFunctionArgs) {
  await requireNotAuthenticated(request)
  let connectionError: LoaderReturnType['connectionError'] = undefined
  const errorCode = new URL(request.url).searchParams.get('error')
  if (errorCode && isErrorCodeKnown(errorCode)) {
    connectionError = {
      code: errorCode,
      message: getApiErrorMessage(errorCode),
    }
  }

  return {
    connectionError,
  }
}

export default function Login() {
  const { connectionError } = useLoaderData() as LoaderReturnType
  const actionData = useActionData<typeof action>()
  return (
    <PageLayoutWithoutHeader
      background={<BackgroundElements showBoth={true} />}
    >
      <div className={'flex h-screen flex-1 flex-col justify-between'}>
        <LoginForm
          connectionError={connectionError}
          showConfirmDialog={actionData?.success}
        />
        <Footer />
      </div>
    </PageLayoutWithoutHeader>
  )
}
